/**
 * The account menu in the top bar and the two things that hang off the session:
 * changing the password, and noticing that the session is gone.
 *
 * Both float above the page — the bar is `position: sticky` and its buttons carry
 * their own cascade, so the dialog and the toast are portalled to `<body>` rather
 * than rendered inside `#bar`.
 */
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import * as api from '../api';
import { useT } from '../i18n';
import { MIN_PASSWORD, passwordProblem } from '../password';
import { useSession } from '../session';

/** A short-lived, non-blocking message at the bottom of the window. */
function Toast({
  text,
  kind,
  action,
  onClose,
}: {
  text: string;
  kind?: 'ok' | 'warn';
  action?: ReactNode;
  onClose(): void;
}) {
  const t = useT();
  return createPortal(
    <div className={'toast' + (kind === 'warn' ? ' warn' : '')} role="status">
      <span className="msg">{text}</span>
      {action}
      <button type="button" className="x" onClick={onClose} aria-label={t('toast.close')}>
        ×
      </button>
    </div>,
    document.body,
  );
}

/**
 * Current + new × 2. The rules are checked here first (`passwordProblem`) so the
 * common mistakes never leave the browser; everything else is the server's answer,
 * with one translation: 401 here means the *current* password was wrong, not that
 * the session died.
 */
export function ChangePasswordDialog({ onClose, onDone }: { onClose(): void; onDone(): void }) {
  const t = useT();
  const [current, setCurrent] = useState('');
  const [pw, setPw] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<api.FormError>(null);
  const [busy, setBusy] = useState(false);
  const firstRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    firstRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const problem = passwordProblem(pw, repeat);
    if (problem) {
      setError({ key: 'pw.err.' + problem, vars: { n: MIN_PASSWORD } });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.changePassword(current, pw);
      onDone();
    } catch (err) {
      if (err instanceof api.ApiError && err.code === 'bad_credentials') setError({ key: 'pw.err.current' });
      else setError({ err });
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="modalwrap" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="modal card" role="dialog" aria-modal="true" aria-labelledby="pwdlg-h">
        <h2 id="pwdlg-h">{t('pw.change')}</h2>
        <p className="hint">{t('pw.change.lead')}</p>
        <form className="stack" onSubmit={e => void submit(e)}>
          <label>
            {t('pw.current')}
            <input
              ref={firstRef}
              type="password"
              autoComplete="current-password"
              required
              value={current}
              onChange={e => setCurrent(e.target.value)}
            />
          </label>
          <label>
            {t('pw.new')} <span className="note">{t('pw.rule', { n: MIN_PASSWORD })}</span>
            <input
              type="password"
              autoComplete="new-password"
              required
              value={pw}
              onChange={e => setPw(e.target.value)}
            />
          </label>
          <label>
            {t('pw.repeat')}
            <input
              type="password"
              autoComplete="new-password"
              required
              value={repeat}
              onChange={e => setRepeat(e.target.value)}
            />
          </label>
          {error ? <p className="err">{api.formMessage(t, error)}</p> : null}
          <div className="row">
            <button type="submit" className="primary" disabled={busy}>
              {busy ? t('auth.busy') : t('pw.save')}
            </button>
            <button type="button" onClick={onClose}>
              {t('pw.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

/**
 * The session died under a signed-in app (the cookie lasts 90 days, but a reset on
 * another device drops every session immediately). It must not interrupt: the plan
 * keeps its local copy in this browser, so this is a prompt, not a redirect.
 * Rendered once, by `Chrome`.
 */
export function SessionExpiredPrompt() {
  const session = useSession();
  const t = useT();
  if (!session.expired) return null;
  return (
    <Toast
      kind="warn"
      text={t('session.expired')}
      onClose={session.dismissExpired}
      action={
        <Link className="linkbtn" to="/login" onClick={session.dismissExpired}>
          {t('session.expired.signin')}
        </Link>
      }
    />
  );
}
